import { EventCodes } from '../../../App/model/event-codes';

/**
 * @ngdoc service
 * @name mx.SolutionBuilderAgent.DeepLinkingService
 * @module mx.SolutionBuilderAgent
 *
 * @description
 * A service provides a way for opening most common dialogs from URL
 */
export class DeepLinkingService {
	static $inject = [
		'$stateParams',
		'mx.shell.EventService',
		'mx.SolutionBuilderAgent.MixedActionHelper',
		'mx.SolutionBuilderAgent.DialogOpenerService',
		'mx.shell.SidePanelService',
		'mx.SolutionBuilderAgent.ContentWidgetService',
		'mx.SolutionBuilderAgent.WidgetManager',
		'mx.shell.AppsService',
		'mx.shell.RoutingService',
		'mx.internationalization',
	];

	constructor($stateParams, eventService, mixedActionHelper, dialogOpener, sidePanelService, contentWidgetService, widgetManager, applicationService, routingService, i18n) {
		Object.assign(this, {
			$stateParams,
			eventService,
			mixedActionHelper,
			dialogOpener,
			sidePanelService,
			contentWidgetService,
			widgetManager,
			applicationService,
			routingService,
			i18n,
		});
	}

	runAction(actionId, context, presetParams, fullScreen) {
		return this.mixedActionHelper.init({
			action: actionId,
			actionContext: [context],
			type: 'action',
			presetFields: presetParams,
			fullScreen: fullScreen,
		})();
	}

	_parseViewOptions(url) {
		try {
			return JSON.parse(decodeURIComponent(/view-options=(.+?)(?:&|$)/gi.exec(url)[1]));
		} catch (e) {
			return null;
		}
	}

	parseViewOptionsFromUrl() {
		return this._parseViewOptions(window.location.search);
	}

	_parsePresetParams(url) {
		try {
			return JSON.parse(decodeURIComponent((url.match(/presetParams=([^&]*)/) || [])[1]));
		} catch (e) {
			return null;
		}
	}

	parsePresetParamsFromUrl() {
		return this._parsePresetParams(window.location.href);
	}

	async processViewOptions(viewOptions) {
		const navigationId = this.$stateParams.navigationId;
		const presetParams = this.parsePresetParamsFromUrl() || {};

		this.eventService.dispatchEvent(EventCodes.TrackTelemetry, {
			eventName: `DeepLinking executed`,
			...Object.fromEntries(Object.entries(viewOptions).map(([key, value]) => [`mx42_uux_deeplinking_${key}`, value])),
		});

		const objectParams = {
			Id: viewOptions.objectId,
			EntityType: viewOptions.type,
			IsArchived: viewOptions.archived === '1',
		};

		switch (viewOptions.viewType) {
			case 'new':
				this.dialogOpener.openNewDialog(objectParams.EntityType, null, null, undefined, presetParams, null, false, {}, false, viewOptions.dialogId);
				break;
			case 'preview':
			case 'edit': {
				const entityType = objectParams.EntityType;

				if (viewOptions.viewType === 'preview') {
					this.dialogOpener.openPreview(
						objectParams,
						{
							navigationId: navigationId,
							widgetId: viewOptions.dialogId,
							hideActions: viewOptions.ext === 1,
							isSingleView: viewOptions.std === 1,
							readOnly: viewOptions.nda === 1,
						},
						true
					);
				} else {
					this.dialogOpener.openEditDialog(
						{
							_id: objectParams.Id,
							_type: objectParams.EntityType,
							_name: '',
						},
						true,
						presetParams,
						undefined,
						{
							widgetId: objectParams.EntityType === entityType ? viewOptions.dialogId : undefined,
							hideActions: viewOptions.ext === 1,
							isSingleView: viewOptions.std === 1,
							readOnly: viewOptions.nda === 1,
						}
					);
				}

				if (viewOptions.runAction) {
					//Old variant for actions
					this.eventService.subscribeOnce(mx.shell.EventCodes.ActionsReady, () => {
						this.runAction(viewOptions.runAction, this.sidePanelService.currentView.context, presetParams, true);
					});
				}
				break;
			}
			case 'action':
				if (viewOptions.actionId) {
					const objectContext = {
						_id: viewOptions.objectId,
						_type: viewOptions.type,
					};
					try {
						const view = await this.widgetManager.getPreviewView(objectContext);
						const widgetId = view?.locals?.widgetId;
						const data = widgetId && objectContext._id && (await this.contentWidgetService.getDialogData(widgetId, objectContext._id));
						this.runAction(viewOptions.actionId, data?.entity || objectContext, presetParams, viewOptions.fullScreen);
					} catch (e) {
						mx.console.warn(e);
					}
				}
				break;
		}
	}

	async _setTitleForExternalOpenedDialog(view, navigationItem) {
		let displayName = view.headerTitle + (view.displayName ? ': ' + view.displayName : '');
		const windowTitle = await this.applicationService.getWindowTitle();

		document.title = this.i18n.getFormatted('solutionBuilderAgent.pageTitle.preview', displayName, navigationItem ? navigationItem.title : '', windowTitle);
		this.eventService.subscribeOnce(mx.SolutionBuilderAgent.Events.ObjectDisplayNameChanged, (e, params) => {
			if (view.id === params.objectId && view.type === params.entityType) {
				displayName = view.headerTitle + (view.displayName ? ': ' + view.displayName : '');
				document.title = this.i18n.getFormatted('solutionBuilderAgent.pageTitle.preview', displayName, navigationItem ? navigationItem.title : '', windowTitle);
			}
		});
	}

	//TODO: refactor this
	async tryOpenDialog(stateName, stateParameters, navigationItem) {
		const windowTitle = await this.applicationService.getWindowTitle();
		// Creating object from outside
		switch (stateName) {
			case 'createObjectExternally': {
				const presetParams = this.parsePresetParamsFromUrl();
				try {
					const view = await this.dialogOpener.openNewDialog(stateParameters.entityType, undefined, undefined, true, presetParams).newDialogOpenedPromise;
					document.title = this.i18n.getFormatted('solutionBuilderAgent.pageTitle.create', view.headerTitle, navigationItem ? navigationItem.title : '', windowTitle);
				} catch (e) {
					mx.logCatch(e);
				}
				break;
			}
			case 'editObjectExternally': {
				const context = {
					_id: stateParameters.objectId,
					_type: stateParameters.entityType,
				};
				const editDialogState = this.dialogOpener.openEditDialog(context, true);
				editDialogState.editDialogOpenedPromise.then((view) => this._setTitleForExternalOpenedDialog(view, navigationItem)).catch(mx.logCatch);
				try {
					const res = await editDialogState.finishDialogInteractionPromise;
					this.dialogOpener.openPreview(res);
				} catch (e) {
					mx.logCatch(e);
				}
				break;
			}
			case 'previewObjectExternally': {
				const view = await this.dialogOpener.openPreview(
					{
						_id: stateParameters.objectId,
						_type: stateParameters.entityType,
						_archived: stateParameters._archived === '1',
					},
					{
						searchPageWidgetId: stateParameters.contentWidgetId,
						navigationId: stateParameters.navigationId,
						widgetId: stateParameters.previewWidgetId,
					},
					true
				);
				this._setTitleForExternalOpenedDialog(view, navigationItem);
				break;
			}
			default:
				document.title = this.i18n.getFormatted('solutionBuilderAgent.pageTitle.searchPage', navigationItem ? navigationItem.title : '', windowTitle);
		}
	}
}
